import type { UpgradeDefinition, UpgradeKind } from "./definitions";

export function calculateUpgradePrice(definition: UpgradeDefinition, level: number): number {
  return Math.ceil(rawPrice(definition, level) * postLevelMultiplier(definition.kind, level) * latePricePressure(definition.kind, level));
}

export function calculateRollbackRefund(definition: UpgradeDefinition, currentLevel: number): number {
  if (currentLevel <= 0) {
    return 0;
  }

  const paidLevel = currentLevel - 1;
  return Math.ceil(rawPrice(definition, paidLevel) * rollbackLegacyMultiplier(definition.kind, paidLevel) * latePricePressure(definition.kind, paidLevel) * 0.8);
}

function rawPrice(definition: UpgradeDefinition, level: number): number {
  if (definition.kind === "GateCount") {
    return [3_500, 180_000, 12_500_000][level] ?? Number.POSITIVE_INFINITY;
  }

  if (definition.kind === "ExtraShell") {
    return [22_000, 18_000_000][level] ?? Number.POSITIVE_INFINITY;
  }

  const base = definition.basePrice * definition.growth ** level;
  if (definition.kind === "MaxCores") {
    return level === 0 ? 75 : base * 1.22 ** (level / 5) * lateCostRamp(level, 24, 1.025);
  }

  if (definition.kind === "OverchargedCore") {
    return base * 1.12 ** (level / 6) * lateCostRamp(level, 18, 1.035);
  }

  if (definition.kind === "CoreValue") {
    return base * 1.18 ** (level / 8) * lateCostRamp(level, 35, 1.045);
  }

  if (definition.kind === "ChargeBonus") {
    return base * 1.2 ** (level / 8) * lateCostRamp(level, 35, 1.052);
  }

  if (definition.kind === "ShellSpeed") {
    return base * 1.16 ** (level / 5);
  }

  if (definition.kind === "GateWidth") {
    return base * 1.28 ** (level / 3);
  }

  if (definition.kind === "CollectorAmp") {
    return base * 1.18 ** (level / 7) * lateCostRamp(level, 30, 1.055);
  }

  if (definition.kind === "CriticalDischarge") {
    return base * 1.14 ** (level / 6) * lateCostRamp(level, 22, 1.04);
  }

  if (definition.kind === "OfflineReactor") {
    return base * 1.1 ** (level / 5) * lateCostRamp(level, 10, 1.03);
  }

  if (definition.kind === "PolaritySync") {
    return base * 1.16 ** (level / 7) * lateCostRamp(level, 26, 1.045);
  }

  return base * 1.12 ** (level / 6) * lateCostRamp(level, 24, 1.035);
}

function lateCostRamp(level: number, start: number, growth: number): number {
  return level <= start ? 1 : growth ** (level - start);
}

function postLevelMultiplier(kind: UpgradeKind, level: number): number {
  if (level < 5) {
    return 1;
  }

  if (level < 10) {
    return kind === "MaxCores" ? 5.2 : 6;
  }

  if (kind === "MaxCores") return 20;
  if (kind === "OverchargedCore") return 26;
  if (kind === "OfflineReactor") return 22;
  if (kind === "ShellSpeed" || kind === "GateWidth" || kind === "CriticalDischarge") return 24;
  if (kind === "ShellLens") return 23;
  if (kind === "PolaritySync") return 25;
  return 26;
}

function latePricePressure(kind: UpgradeKind, level: number): number {
  if (level < 20) {
    return 1;
  }

  const exponent = level - 19;
  if (kind === "OverchargedCore") return 1.052 ** exponent;
  if (kind === "MaxCores") return 1.025 ** exponent;
  if (kind === "CoreValue" || kind === "ChargeBonus" || kind === "CollectorAmp") return 1.024 ** exponent;
  if (kind === "CriticalDischarge") return 1.025 ** exponent;
  if (kind === "ShellSpeed" || kind === "GateWidth") return 1.018 ** exponent;
  if (kind === "OfflineReactor") return 1.015 ** exponent;
  if (kind === "PolaritySync") return 1.022 ** exponent;
  if (kind === "ShellLens") return 1.019 ** exponent;
  return 1.02 ** exponent;
}

function rollbackLegacyMultiplier(kind: UpgradeKind, level: number): number {
  if (level < 5) {
    return 1;
  }

  if (level < 10) {
    return kind === "MaxCores" ? 4.5 : 5;
  }

  if (kind === "MaxCores" || kind === "OverchargedCore") return 16;
  if (kind === "OfflineReactor") return 18;
  if (kind === "ShellSpeed" || kind === "GateWidth" || kind === "CriticalDischarge") return 20;
  if (kind === "PolaritySync" || kind === "ShellLens") return 21;
  return 22;
}
